import { useEffect, useState } from "react";
import Header from "./Header";
import { fetchTokenMarket, fetchTokenChanges } from "~/api/endpoints";
import { FooterBtn } from "./FooterBtn";
import Info from "./Info";
import { useSearchParams } from "react-router";
import TokenChange from "./TokenChange";
import YourBalance from "./YourBalance";
import clsx from "clsx";
import { TIME_GAP, K_TYPE } from "./constants";
import SimpleSegmentedControl from "../Market/SimpleSegmentedControl/SimpleSegmentedControl";
import { hapticFeedback } from "@telegram-apps/sdk-react";
import useSWR from "swr";
import TokenCharts from "./TokenCharts";
import { CHAIN_IDS } from "@uxuycom/web3-tg-sdk";
import { Page } from "~/components/Page/Page";
import FooterTxsPopView from "./FooterTxsPopView";

const localStorageKey = "uxuy-dex-k-detail-tab";
const localStorageActiveCodeKey =
    "__uxuy_dex_cached_k_detail_page_active_code__";

export default function KDetail() {
    const [searchParams] = useSearchParams();
    const chain_id = searchParams.get("chain_id");
    const contract = searchParams.get("contract");
    const isSolana = chain_id === String(CHAIN_IDS.SOLANA);
    const oneWeekTimeGapCode = TIME_GAP[TIME_GAP.length - 1].value;
    const oneDayTimeGapCode = TIME_GAP[TIME_GAP.length - 2].value;
    const cachedActivityCode = window.localStorage.getItem(
        localStorageActiveCodeKey
    );
    const caculatedActivityCode = isSolana
        ? cachedActivityCode == String(oneWeekTimeGapCode)
            ? String(oneDayTimeGapCode)
            : cachedActivityCode
        : cachedActivityCode;
    const initialActivityCode =
        caculatedActivityCode != null
            ? String(caculatedActivityCode)
            : String(TIME_GAP[1].value);
    const [activeCode, setActiveCode] = useState<string | number>(
        initialActivityCode
    );
    const localActiveTypeCode =
        window.localStorage.getItem(localStorageKey) || K_TYPE[0].value;
    const [activeTypeCode, setActiveTypeCode] = useState(localActiveTypeCode);
    const [key, setKey] = useState(0);

    useEffect(() => {
        const isSolana = chain_id === String(CHAIN_IDS.SOLANA);
        const cachedActivityCode = window.localStorage.getItem(
            localStorageActiveCodeKey
        );
        if (isSolana) {
            if (cachedActivityCode == String(oneWeekTimeGapCode)) {
                setActiveCode(String(oneDayTimeGapCode));
            }
        } else {
            setActiveCode((oldCode) => {
                return cachedActivityCode && cachedActivityCode !== oldCode
                    ? cachedActivityCode
                    : oldCode;
            });
        }
    }, [chain_id, oneDayTimeGapCode, oneWeekTimeGapCode]);

    const tokenSelectCallback = () => {
        setKey(key + 1);
    };

    const initTokenData = (args: any) => {
        const [, chain_id, contract] = args;
        return fetchTokenMarket(chain_id, contract).then(
            (res) => res?.data || {}
        );
    };

    const {
        data: tokenInfo,
        mutate,
        isLoading,
    } = useSWR(
        ["k-detail:fetchTokenMarket", chain_id, contract],
        initTokenData,
        { refreshInterval: 8000 }
    );
    const riskLevel = tokenInfo?.token_risk?.risk_level ?? 1;

    const initData = ([chain_id, contract]: any) =>
        fetchTokenChanges(chain_id, contract).then((res) => res?.data);

    const { data: tokenChangeData } = useSWR(
        [chain_id, contract, "k-detail:token_changes"],
        initData,
        {
            fallbackData: {
                txn: [],
                vol: [],
            },
        }
    );

    const [isFavorite, setIsFavorite] = useState(
        tokenInfo?.token_base?.favorite ?? false
    );
    useEffect(() => {
        if (typeof tokenInfo?.token_base?.favorite === "boolean") {
            setIsFavorite(tokenInfo?.token_base?.favorite);
        }
    }, [tokenInfo?.token_base?.favorite]);

    const updateFavoriteCache = (newFavorite: boolean) => {
        if (!tokenInfo) return;
        mutate(
            {
                ...tokenInfo,
                token_base: {
                    ...tokenInfo.token_base,
                    favorite: newFavorite,
                },
            },
            { revalidate: true }
        );
    };

    const updateTokenPrice = (newPrice: number) => {
        if (isLoading) return;
        if (!tokenInfo) return;
        mutate(
            {
                ...tokenInfo,
                token_market: {
                    ...tokenInfo.token_market,
                    price: newPrice,
                },
            },
            { revalidate: false }
        );
    };

    const [showPopView, setShowPopView] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowPopView(true);
        }, 3000);

        return () => {
            clearTimeout(timer);
        };
    }, []);

    return (
        <Page>
            <Header
                tokenInfo={tokenInfo}
                chain_id={chain_id}
                contract={contract}
                tokenSelectCallback={tokenSelectCallback}
            />

            <TokenCharts
                activeCode={activeCode}
                activeTypeCode={activeTypeCode}
                chain_id={chain_id}
                contract={contract}
                tokenInfo={tokenInfo}
                updateTokenPrice={updateTokenPrice}
            />

            <div className="flex items-center justify-between h-[44px] px-[16px]">
                <SimpleSegmentedControl
                    className={clsx(isSolana ? "w-[160px]" : "w-[200px]")}
                    options={TIME_GAP.filter((item) =>
                        isSolana ? item.value != oneWeekTimeGapCode : true
                    ).map((item) => ({
                        label: item.name,
                        value: String(item.value),
                    }))}
                    selectedValue={String(activeCode)}
                    onSelectedValueChange={(type) => {
                        setActiveCode(String(type));
                        window.localStorage.setItem(
                            localStorageActiveCodeKey,
                            String(type)
                        );
                        hapticFeedback.impactOccurred("light");
                    }}
                />
                <SimpleSegmentedControl
                    className="w-[80px]"
                    options={K_TYPE.map((item) => ({
                        label: item.name,
                        value: item.value,
                    }))}
                    selectedValue={activeTypeCode}
                    onSelectedValueChange={(type) => {
                        setActiveTypeCode(type.toString());
                        window.localStorage.setItem(
                            localStorageKey,
                            type.toString()
                        );
                        hapticFeedback.impactOccurred("light");
                    }}
                />
            </div>

            <TokenChange
                tokenChangeData={tokenChangeData}
                className="mt-[8px]"
                isSolana={chain_id === String(CHAIN_IDS.SOLANA)}
            />
            <YourBalance chain_id={chain_id} contract={contract} />

            <Info
                chain_id={chain_id}
                contract={contract}
                className="mt-[24px]"
            />

            {showPopView && (
                <FooterTxsPopView
                    chain_id={chain_id ?? ""}
                    contract={contract ?? ""}
                />
            )}

            <FooterBtn
                className={clsx("!bg-bg_0", "h-[54px]")}
                ifPlaceholder={true}
                chain_id={chain_id ?? ""}
                contract={contract ?? ""}
                risk_level={riskLevel}
                isFavorite={isFavorite}
                setIsFavorite={setIsFavorite}
                updateFavoriteCache={updateFavoriteCache}
            />
        </Page>
    );
}
